export const FILTERS = ['all', 'active', 'closed', 'archived'];

export const USE_CHAT_MOCKS = false; //process.env.REACT_APP_USE_CHAT_MOCKS === 'true';

export const formatDateTime = (value) => {
    if (!value) return 'Unknown time';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown time';

    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    }).format(date);
};

export const formatDateOnly = (value) => {
    if (!value) return 'Unknown date';

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown date';

    return new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    }).format(date);
};

export const getDisplayName = (thread) =>
    [
        thread?.patient?.name,
        [thread?.patient?.first_name, thread?.patient?.last_name].filter(Boolean).join(' ').trim(),
        thread?.customerEmail,
        'Unknown Customer'
    ].find((value) => value && value.length > 0);

export const getThreadEmail = (thread) =>
    thread?.patient?.email || thread?.customerEmail || 'No email available';

export const getThreadStatusClasses = (status) => {
    switch (status) {
        case 'active':
            return 'bg-[#DDF4E4] text-[#2F5E38]';
        case 'closed':
            return 'bg-[#F4E6D6] text-[#825A22]';
        case 'archived':
            return 'bg-[#ECECEC] text-[#5C5C5C]';
        default:
            return 'bg-[#F1F1F1] text-[#6B6B6B]';
    }
};

export const getMessageStatusClasses = (status) => {
    switch (status) {
        case 'delivered':
            return 'text-[#2F5E38]';
        case 'sent':
            return 'text-[#6B6B6B]';
        case 'pending':
            return 'text-[#825A22]';
        case 'failed':
            return 'text-[#B42318]';
        default:
            return 'text-[#6B6B6B]';
    }
};

export const getLastMessageBadge = (thread) => {
    const status = thread?.lastMessageStatus || 'sent';

    if (status === 'delivered') {
        // return {
        //   label: thread?.lastMessageIsRead ? 'Read' : 'Delivered',
        //   classes: thread?.lastMessageIsRead
        //     ? 'bg-[#E7F6EC] text-[#256B45]'
        //     : 'bg-[#EEF2F6] text-[#4C6272]'
        // };
        return { label: "Delivered", classes: 'bg-[#EEF2F6] text-[#4C6272]' };
    }

    switch (status) {
        case 'sent':
            return { label: 'Sent', classes: 'bg-[#F1F3F5] text-[#5F6C76]' };
        case 'pending':
            return { label: 'Pending', classes: 'bg-[#FFF3DD] text-[#8A5A00]' };
        case 'failed':
            return { label: 'Failed', classes: 'bg-[#FEE4E2] text-[#B42318]' };
        default:
            return { label: status, classes: 'bg-[#F1F3F5] text-[#5F6C76]' };
    }
};

const normalizeThreadRecord = (thread) => {
    if (!thread?.chatToken) return null;

    const normalizedLastMessage = thread?.lastMessage || null;

    return {
        ...thread,
        _id: thread?._id || thread?.threadId || normalizedLastMessage?._id || null,
        threadId: thread?.threadId || thread?._id || null,
        lastMessage: normalizedLastMessage,
        lastMessageText:
            thread?.lastMessageText ||
            normalizedLastMessage?.message ||
            '',
        lastMessageStatus:
            thread?.lastMessageStatus ||
            normalizedLastMessage?.status ||
            thread?.status ||
            'sent',
        lastMessageIsRead:
            typeof thread?.lastMessageIsRead === 'boolean'
                ? thread.lastMessageIsRead
                : normalizedLastMessage?.is_read,
        lastMessageAt:
            thread?.lastMessageAt ||
            normalizedLastMessage?.createdAt ||
            thread?.updatedAt ||
            null,
        updatedAt:
            thread?.updatedAt ||
            normalizedLastMessage?.updatedAt ||
            thread?.lastMessageAt ||
            null,
        hasUnread: Boolean(thread?.hasUnread)
    };
};

export const normalizeThreadsPayload = (payload) => {
    const threads =
        Array.isArray(payload) ? payload :
            Array.isArray(payload?.data) ? payload.data :
                Array.isArray(payload?.data?.threads) ? payload.data.threads :
                    Array.isArray(payload?.results) ? payload.results :
                        Array.isArray(payload?.items) ? payload.items :
                            [];

    return threads.map((thread) => normalizeThreadRecord(thread)).filter(Boolean);
};

export const normalizeMessagesPayload = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.messages)) return payload.data.messages;
    if (Array.isArray(payload?.messages)) return payload.messages;
    if (Array.isArray(payload?.results)) return payload.results;
    return [];
};

export const normalizeMessagePayload = (payload) => {
    if (!payload) return null;
    if (payload?.chatToken && payload?.message) return payload;
    if (payload?.data?.chatToken && payload?.data?.message) return payload.data;
    if (payload?.message?.chatToken && payload?.message?.message) return payload.message;
    if (payload?.data?.message?.chatToken && payload?.data?.message?.message) return payload.data.message;
    return null;
};

export const normalizeThreadPayload = (payload) => {
    const thread =
        !payload ? null :
            payload?.chatToken ? payload :
                payload?.data?.chatToken ? payload.data :
                    payload?.thread?.chatToken ? payload.thread :
                        payload?.data?.thread?.chatToken ? payload.data.thread :
                            null;

    return normalizeThreadRecord(thread);
};

export const sortThreads = (threads) =>
    [...threads].sort(
        (a, b) => new Date(b?.lastMessageAt || b?.updatedAt || 0) - new Date(a?.lastMessageAt || a?.updatedAt || 0)
    );

export const sortMessages = (messages) =>
    [...messages].sort((a, b) => new Date(a?.createdAt || 0) - new Date(b?.createdAt || 0));

const isSameMessage = (existingMessage, incomingMessage) => {
    if (!existingMessage || !incomingMessage) return false;

    const existingSesMessageId = existingMessage?.sesMessageId;
    const incomingSesMessageId = incomingMessage?.sesMessageId;

    if (existingSesMessageId && incomingSesMessageId && existingSesMessageId === incomingSesMessageId) {
        return true;
    }

    return Boolean(existingMessage?._id && incomingMessage?._id && existingMessage._id === incomingMessage._id);
};

export const upsertMessage = (messages, incomingMessage) => {
    if (!incomingMessage) return messages;

    const existingIndex = messages.findIndex((message) => isSameMessage(message, incomingMessage));

    if (existingIndex === -1) {
        return [...messages, incomingMessage];
    }

    const nextMessages = [...messages];
    nextMessages[existingIndex] = {
        ...nextMessages[existingIndex],
        ...incomingMessage
    };

    return nextMessages;
};

export const updateMessageDelivery = (messages, payload) => {
    if (!payload) return messages;

    return messages.map((message) => {
        const sesMessageIdMatches = payload?.sesMessageId && message?.sesMessageId === payload.sesMessageId;
        const messageIdMatches = payload?.messageId && message?._id === payload.messageId;

        if (!sesMessageIdMatches && !messageIdMatches) {
            return message;
        }

        return {
            ...message,
            status: payload?.status || message?.status
        };
    });
};

export const upsertThread = (threads, incomingThread) => {
    const normalizedThread = normalizeThreadRecord(incomingThread);

    if (!normalizedThread?.chatToken) return threads;

    const existingIndex = threads.findIndex((thread) => thread?.chatToken === normalizedThread.chatToken);

    if (existingIndex === -1) {
        return [...threads, normalizedThread];
    }

    const nextThreads = [...threads];
    nextThreads[existingIndex] = {
        ...nextThreads[existingIndex],
        ...normalizedThread
    };

    return nextThreads;
};

export const buildThreadPreviewFromMessage = (message) => {
    if (!message?.chatToken) return null;

    return {
        chatToken: message.chatToken,
        lastMessage: message,
        lastMessageAt: message.createdAt || new Date().toISOString(),
        updatedAt: message.updatedAt || message.createdAt || new Date().toISOString(),
        lastMessageText: message.message || '',
        lastMessageStatus: message.status || 'sent',
        hasUnread: message.direction !== 'outbound'
    };
};
